// S7.4 — Global ErrorBoundary.
//
// React unmounts the entire tree on any uncaught render exception. Without a
// boundary, the result in production is a fully blank page (the body's
// `bg-background` is dark, so it reads as "completely black") with zero way
// for the founder to recover except hard refresh — exactly the symptom we
// hit with the `concurrency:updated` contract bug.
//
// This boundary catches that class of failure and replaces the void with:
//   • A plain-English headline ("Something broke")
//   • The error message (one line)
//   • A "Reload" button (the founder's first instinct, made visible)
//   • A "Copy details" button (exposes message + component stack so the
//     next bug report includes the diagnostic for free)
//
// Founder-first §5: no jargon, no stack traces in the foreground, a clear
// next action. The full stack is hidden behind "Copy details" — Santi never
// has to read it; pasting it into a chat with the agent is enough.
//
// The boundary also `console.error`s in case DevTools is open. Frontend
// telemetry (Sentry, etc.) would hook here in the future.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '../i18n';

export interface ErrorBoundaryProps {
  children: ReactNode;
  // Optional override — caller can label the surface (e.g. "Chat view")
  // so multi-boundary setups can hint where the failure happened.
  surface?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    // Loud in DevTools so reproducing the bug while watching the console
    // surfaces the original throw + React's component path immediately.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.surface ? ` · ${this.props.surface}` : ''}]`,
      error,
      info.componentStack,
    );
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleCopy = async (): Promise<void> => {
    const { error, componentStack } = this.state;
    if (!error) return;
    const payload = [
      t('errorBoundary.crashReport'),
      `Time: ${new Date().toISOString()}`,
      `URL: ${window.location.href}`,
      `User-Agent: ${navigator.userAgent}`,
      ``,
      `Error: ${error.message}`,
      ``,
      `Stack:`,
      error.stack ?? '(no stack)',
      ``,
      `Component stack:`,
      componentStack ?? '(no component stack)',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      // Clipboard API can fail in non-HTTPS or denied-permission contexts.
      // Fall back to a textarea select-all so the user can copy manually.
      const ta = document.createElement('textarea');
      ta.value = payload;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* swallow — last resort already tried */
      }
      document.body.removeChild(ta);
    }
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const { error } = this.state;
    return (
      <div
        role="alert"
        className="h-full w-full flex items-center justify-center bg-background text-foreground p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full rounded-md border border-status-error/40 bg-status-error/10 p-6 space-y-4">
          <h1 className="text-body-chat font-semibold text-status-error">
            {t('errorBoundary.title')}
          </h1>
          <p className="text-body text-foreground leading-relaxed">
            {t('errorBoundary.body')}
          </p>
          <pre
            className="text-xs text-muted-foreground whitespace-pre-wrap break-words bg-background border border-border/50 rounded p-2 max-h-32 overflow-y-auto" /* design-discipline: kept as text-xs — developer-mode error stack */
            data-testid="error-boundary-message"
          >
            {error.message || error.name || 'Unknown error'}
          </pre>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 px-4 py-2 rounded bg-action hover:bg-action/90 text-action-foreground text-body font-medium focus:outline-none focus:ring-2 focus:ring-action"
              data-testid="error-boundary-reload"
            >
              {t('errorBoundary.reload')}
            </button>
            <button
              type="button"
              onClick={() => void this.handleCopy()}
              className="flex-1 px-4 py-2 rounded border border-border text-muted-foreground hover:bg-card text-body focus:outline-none focus:ring-2 focus:ring-border"
              data-testid="error-boundary-copy"
            >
              {t('errorBoundary.copyDetails')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
